import { useState, useEffect } from 'react';
import { StateStats, CityStats } from '../hooks/useBusinessMetrics';

interface GeographicDistributionProps {
  stateStats: StateStats[];
  cityStats: CityStats[];
  loading: boolean;
}

export default function GeographicDistribution({
  stateStats,
  cityStats,
  loading,
}: GeographicDistributionProps) {
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'state' | 'city'>('state');

  // Debug logging
  useEffect(() => {
    console.log('[GeographicDistribution] Props updated:', {
      loading,
      stateCount: stateStats.length,
      cityCount: cityStats.length,
      firstState: stateStats[0],
    });
  }, [stateStats, cityStats, loading]);

  // Filter cities by selected state
  const filteredCities = selectedState
    ? cityStats.filter(city => city.state === selectedState)
    : cityStats;

  // Get top cities (limit to 10 for initial view)
  const displayCities = selectedState ? filteredCities : cityStats.slice(0, 10);

  const handleStateClick = (state: string) => {
    setSelectedState(selectedState === state ? null : state);
    setViewMode('city');
  };

  const clearStateFilter = () => {
    setSelectedState(null);
    setViewMode('state');
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Geographic Distribution</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('state')}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'state'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            By State
          </button>
          <button
            onClick={() => setViewMode('city')}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'city'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            By City
          </button>
        </div>
      </div>

      {selectedState && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-600">
            Showing cities in <span className="font-semibold">{selectedState}</span>
          </span>
          <button
            onClick={clearStateFilter}
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            Clear filter
          </button>
        </div>
      )}

      {viewMode === 'state' ? (
        /* State View */
        <div className="overflow-hidden">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading state data...</div>
          ) : stateStats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No state data available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      State
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Free
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Starter
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Steady
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Growth
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stateStats.map((stat) => (
                    <tr
                      key={stat.state}
                      onClick={() => handleStateClick(stat.state)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                        {stat.state}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                        {stat.total}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600">
                        {stat.active}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                        {stat.free}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {stat.starter}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600">
                        {stat.steady}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-amber-600">
                        {stat.growth}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* City View */
        <div className="overflow-hidden">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading city data...</div>
          ) : displayCities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {selectedState
                ? `No cities found in ${selectedState}`
                : 'No city data available'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        State
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Free
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Starter
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Steady
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Growth
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayCities.map((stat, idx) => (
                      <tr key={`${stat.city}-${stat.state}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {stat.city}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          <button
                            onClick={() => handleStateClick(stat.state)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {stat.state}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                          {stat.total}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600">
                          {stat.active}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                          {stat.free}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                          {stat.starter}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600">
                          {stat.steady}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-amber-600">
                          {stat.growth}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!selectedState && cityStats.length > 10 && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-500">
                    Showing top 10 cities. Click a state to see all cities in that state.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
